import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { toast } from "react-toastify";
import { verifyPaymentApi } from "../../services/api/pitbPayzenApi";

export default function PsidDetailsDialog({
  open,
  onClose,
  psidData,
  selectedPlan,
  onSuccess,
}) {
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState(null);

  const consumerNo = psidData?.consumerNumber || psidData?.psid || "";

  const handleManualVerifyPayment = async () => {
    if (!consumerNo) {
      return;
    }

    setVerifying(true);
    setVerifyStatus(null);

    try {
      const response = await verifyPaymentApi(consumerNo);

      const isPaid =
        response.data?.status === "PAID" ||
        response.data?.status === "OK" ||
        response.data?.psidStatus === "PAID";

      if (isPaid) {
        toast.success(
          "Payment verified successfully! Your subscription is now active.",
        );

        setVerifyStatus({
          type: "success",
          text: "Payment verified successfully! Your subscription is now active.",
        });

        setTimeout(() => {
          const activatedId =
            response.data?.planId || selectedPlan?.id || selectedPlan?.title;

          if (onSuccess) {
            onSuccess(activatedId);
          }
        }, 1500);
      } else {
        setVerifyStatus({
          type: "info",
          text: "Payment not detected yet. 1Bill can take up to 2 minutes to sync. Please try again shortly.",
        });
      }
    } catch (error) {
      console.error("Payment verification failed:", error);

      setVerifyStatus({
        type: "error",
        text:
          error.response?.data?.message ||
          "Could not verify payment status. Please try again.",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(consumerNo);

      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy PSID:", error);
    }
  };

  const handleClose = () => {
    setVerifyStatus(null);
    onClose();
  };

  if (!psidData) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant='h6' fontWeight='bold'>
          Payment Voucher Generated
        </Typography>

        <IconButton onClick={handleClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity='info' sx={{ mb: 2 }}>
          Please use this PSID / Consumer Number to pay via 1Bill on your Mobile
          Banking App, ATM, or OTC.
        </Alert>

        {verifyStatus && (
          <Alert severity={verifyStatus.type} sx={{ mb: 2 }}>
            {verifyStatus.text}
          </Alert>
        )}

        <Box
          sx={{
            p: 2,
            bgcolor: "#f0f4f8",
            borderRadius: 2,
            textAlign: "center",
            mb: 2,
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            PSID / Consumer Number
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mt: 0.5,
            }}
          >
            <Typography
              variant='h6'
              fontWeight='bold'
              letterSpacing={1}
              color='#003D78'
            >
              {consumerNo || "N/A"}
            </Typography>

            <Tooltip title={copied ? "Copied!" : "Copy Number"}>
              <IconButton size='small' onClick={handleCopy} color='primary'>
                <ContentCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {psidData.challanNumber && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 1,
              py: 0.5,
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              Challan No:
            </Typography>

            <Typography variant='body2' fontWeight='bold'>
              {psidData.challanNumber}
            </Typography>
          </Box>
        )}

        {psidData.amount && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 1,
              py: 0.5,
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              Amount Due:
            </Typography>

            <Typography variant='body2' fontWeight='bold' color='primary'>
              {psidData.amount} PKR
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          color='inherit'
          fullWidth
          disabled={verifying}
        >
          Close
        </Button>

        <Button
          onClick={handleManualVerifyPayment}
          variant='contained'
          fullWidth
          disabled={verifying}
          sx={{
            backgroundColor: "#003D78",
          }}
        >
          {verifying ? (
            <CircularProgress size={22} color='inherit' />
          ) : (
            "Verify Payment"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
